/**
 * Merchant grouping colors for route stop lists.
 *
 * A merchant's tasks share one pickup location and are handled together at a single
 * stop, so when a merchant has 2+ stops in a route we color-band them to make the group
 * visually obvious in the ordered stop list. Single-stop merchants and depot stops get
 * no band, keeping the list calm.
 */

interface StopLike {
  merchant_name?: string | null;
  type?: string;
}

// Distinct hues, drawn from the palettes already used across the app (routes/zones).
const MERCHANT_GROUP_PALETTE = [
  '#8B5CF6', // violet
  '#3B82F6', // blue
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
  '#F97316', // orange
  '#6366F1', // indigo
];

function getMerchantKey(stop: StopLike): string | null {
  if (stop.type === 'depot') return null;
  const name = stop.merchant_name?.trim();
  return name ? name : null;
}

/**
 * Build a map of merchant name -> hex color for merchants that have 2 or more stops in
 * the given (ordered) route — i.e. real groups worth highlighting. Single-stop merchants
 * and depot stops are absent from the map; callers treat "no entry" as "no band".
 * Colors are assigned in order of first appearance so banding is stable for a route.
 */
export function buildMerchantGroupColorMap(stops: StopLike[]): Map<string, string> {
  const counts = new Map<string, number>();
  for (const stop of stops) {
    const key = getMerchantKey(stop);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const colors = new Map<string, string>();
  let next = 0;
  for (const stop of stops) {
    const key = getMerchantKey(stop);
    if (key && (counts.get(key) ?? 0) >= 2 && !colors.has(key)) {
      colors.set(key, MERCHANT_GROUP_PALETTE[next % MERCHANT_GROUP_PALETTE.length]);
      next += 1;
    }
  }
  return colors;
}

/**
 * The band color for a single stop given a route's color map, or undefined when the stop
 * isn't part of a multi-stop merchant group (or is a depot).
 */
export function getMerchantBandColor(
  stop: StopLike,
  colorMap: Map<string, string>
): string | undefined {
  const key = getMerchantKey(stop);
  return key ? colorMap.get(key) : undefined;
}
